/**
 * Vista admin de comprobantes PAC subidos por los comercios. Permite:
 *  - Ver lista paginada con estado de revisión.
 *  - Visualizar el comprobante (presigned download).
 *  - Marcar como revisado y registrar el monto máximo declarado por el cliente.
 */

import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';

interface PacReceiptRow {
  id: string;
  companyId: string;
  companyName: string | null;
  bankCode: string;
  accountNumber: string;
  uploadedAt: string;
  adminReviewedAt: string | null;
  adminMaxAmountAssigned: string | null;
}

interface PacReceiptList {
  data: PacReceiptRow[];
  page: number;
  pageSize: number;
  total: number;
}

const PAGE_SIZE = 20;

const fmtDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtMoney = (raw: string | null) => {
  if (raw === null || raw === '') return '—';
  const n = Number(raw);
  if (Number.isNaN(n)) return raw;
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 2 }).format(n);
};

export default function PacComprobantesPage() {
  const [filter, setFilter] = useState<'all' | 'pending' | 'reviewed'>('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PacReceiptList | null>(null);
  const [loading, setLoading] = useState(true);

  const [previewing, setPreviewing] = useState<PacReceiptRow | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewContentType, setPreviewContentType] = useState<string | null>(null);
  const [maxAmountInput, setMaxAmountInput] = useState('');
  const [savingReview, setSavingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      if (filter === 'pending') params.set('reviewed', 'false');
      if (filter === 'reviewed') params.set('reviewed', 'true');
      const url = `${getAdminEndpointUrl('pacReceipts', 'list')}?${params.toString()}`;
      const result = await adminHttpClient.get<PacReceiptList>(url);
      setData(result);
    } catch (err) {
      console.error('Error cargando comprobantes PAC:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const openPreview = async (row: PacReceiptRow) => {
    setPreviewing(row);
    setPreviewUrl(null);
    setPreviewContentType(null);
    setMaxAmountInput(row.adminMaxAmountAssigned ?? '');
    setReviewError(null);
    try {
      const url = getAdminEndpointUrl('pacReceipts', 'downloadUrl').replace('{id}', row.id);
      const res = await adminHttpClient.get<{ url: string; contentType: string | null }>(url);
      setPreviewUrl(res.url);
      setPreviewContentType(res.contentType);
    } catch (err) {
      console.error('Error obteniendo URL de descarga:', err);
      setReviewError('No se pudo cargar el comprobante');
    }
  };

  const closePreview = () => {
    setPreviewing(null);
    setPreviewUrl(null);
    setPreviewContentType(null);
    setMaxAmountInput('');
    setReviewError(null);
  };

  const submitReview = async (clearAmount: boolean) => {
    if (!previewing) return;
    setSavingReview(true);
    setReviewError(null);
    try {
      const url = getAdminEndpointUrl('pacReceipts', 'update').replace('{id}', previewing.id);
      const body: { maxAmount: string | null } = { maxAmount: null };
      if (!clearAmount && maxAmountInput.trim() !== '') {
        if (!/^\d+(\.\d{1,2})?$/.test(maxAmountInput.trim())) {
          throw new Error('Formato de monto inválido (ej: 50000 o 50000.00)');
        }
        body.maxAmount = maxAmountInput.trim();
      }
      await adminHttpClient.put(url, body);
      closePreview();
      void fetchList();
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'Error guardando revisión');
    } finally {
      setSavingReview(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Comprobantes PAC</h1>
          <p className="mt-1 text-sm text-slate-500">Revisa los comprobantes de activación de mandato PAC subidos por los comercios y registra el monto máximo declarado.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-900">
            {(['all', 'pending', 'reviewed'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => {
                  setFilter(f);
                  setPage(1);
                }}
                className={`rounded-md px-3 py-1 text-xs font-medium transition ${
                  filter === f ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                {f === 'all' ? 'Todos' : f === 'pending' ? 'Pendientes' : 'Revisados'}
              </button>
            ))}
          </div>
          <span className="text-xs text-slate-500">{data ? `${data.total} comprobante(s)` : ''}</span>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
              <tr>
                <th className="px-4 py-3">Empresa</th>
                <th className="px-4 py-3">Banco / Cuenta</th>
                <th className="px-4 py-3">Subido</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Monto máx.</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Cargando…
                  </td>
                </tr>
              ) : !data || data.data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Sin comprobantes que mostrar.
                  </td>
                </tr>
              ) : (
                data.data.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 dark:text-slate-100">{r.companyName ?? '—'}</p>
                      <p className="font-mono text-[11px] text-slate-500">{r.companyId}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs">Banco {r.bankCode}</p>
                      <p className="font-mono text-xs">{r.accountNumber}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{fmtDateTime(r.uploadedAt)}</td>
                    <td className="px-4 py-3">
                      {r.adminReviewedAt ? (
                        <span className="inline-flex items-center gap-1 rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          <span className="material-symbols-outlined text-sm">check_circle</span>
                          Revisado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                          <span className="material-symbols-outlined text-sm">schedule</span>
                          Pendiente
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">{fmtMoney(r.adminMaxAmountAssigned)}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void openPreview(r)}
                        className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                      >
                        Ver / revisar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {data && data.total > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-xs dark:border-slate-800">
              <span className="text-slate-500">
                Página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50 dark:border-slate-700"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50 dark:border-slate-700"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {previewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="flex h-full max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 dark:border-slate-800">
              <div>
                <h2 className="text-base font-semibold">Comprobante de {previewing.companyName ?? previewing.companyId}</h2>
                <p className="text-xs text-slate-500">
                  Banco {previewing.bankCode} · {previewing.accountNumber}
                </p>
              </div>
              <button type="button" onClick={closePreview} className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="flex flex-1 overflow-hidden">
              <div className="flex flex-1 items-center justify-center bg-slate-100 p-3 dark:bg-slate-950">
                {previewUrl ? (
                  previewContentType?.startsWith('image/') ? (
                    <img src={previewUrl} alt="Comprobante PAC" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <iframe src={previewUrl} title="Comprobante PAC" className="h-full w-full bg-white" />
                  )
                ) : (
                  <span className="text-sm text-slate-500">Cargando comprobante…</span>
                )}
              </div>
              <div className="w-72 border-l border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="text-sm font-medium">Revisión</h3>
                <p className="mt-1 text-xs text-slate-500">Si el cliente declaró un monto máximo distinto al recomendado (sin tope), regístralo aquí.</p>

                <label className="mt-4 block text-xs font-medium">Monto máximo declarado (CLP)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={maxAmountInput}
                  onChange={(e) => setMaxAmountInput(e.target.value)}
                  placeholder="Ej: 500000"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800"
                />

                {reviewError && <p className="mt-3 text-xs text-red-600">{reviewError}</p>}

                <div className="mt-4 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => void submitReview(false)}
                    disabled={savingReview}
                    className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingReview ? 'Guardando…' : 'Marcar revisado'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitReview(true)}
                    disabled={savingReview}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    Revisar sin monto
                  </button>
                </div>

                <p className="mt-4 text-[11px] leading-tight text-slate-500">
                  Al guardar, el monto se replica al mandato vigente de esta cuenta (si existe). Si no hay mandato aún, queda registrado sólo en el comprobante.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
